import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { currentUserAtom } from '../auth/AuthAtoms';
import { userItemsAtom, userItemCapsAtom } from '../atoms/itemAtoms';
import { updateGameSpeed, updateDiscordUsername } from '../api/user';
import { getGoogleSignInUrl, unlinkSocialProvider, linkAppleAccount } from '../api/auth';
import { userTrophiesAtom } from '../atoms/trophyAtoms';
import { userBadgesAtom } from '../atoms/badgeAtoms';
import GLOBALS from '../config/globals';
import TrophyCase from '../components/TrophyCase';
import BadgeCatalog from '../components/BadgeCatalog';
import { toast } from 'react-toastify';

const AccountPage = () => {
    const [currentUser, setCurrentUser] = useAtom(currentUserAtom);
    const userItems = useAtomValue(userItemsAtom);
    const itemCaps = useAtomValue(userItemCapsAtom);
    const [gameSpeed, setGameSpeed] = useState(currentUser?.gameSpeed || GLOBALS.GAME_SPEED.NORMAL.name);
    const [discordUsername, setDiscordUsername] = useState(currentUser?.discordUsername || '');
    const trophies = useAtomValue(userTrophiesAtom);
    const badges = useAtomValue(userBadgesAtom);
    const levelInfo = currentUser?.levelInfo;

    useEffect(() => {
        if (currentUser) {
            setGameSpeed(currentUser.gameSpeed || GLOBALS.GAME_SPEED.NORMAL.name);
            setDiscordUsername(currentUser.discordUsername || '');
        }
    }, [currentUser]);

    const handleGameSpeedChange = async e => {
        const selectedSpeed = e.target.value;
        setGameSpeed(selectedSpeed);

        try {
            await updateGameSpeed(selectedSpeed);
            setCurrentUser(prev => ({ ...prev, gameSpeed: selectedSpeed }));
            toast.success('Game speed updated successfully!');
        } catch (err) {
            console.error('Error updating game speed:', err);
            toast.error('Failed to update game speed: ' + err.message);
        }
    };

    const handleDiscordUsernameChange = async e => {
        e.preventDefault();

        try {
            await updateDiscordUsername(discordUsername);
            setCurrentUser(prev => ({ ...prev, discordUsername }));
            toast.success('Discord username updated successfully!');
        } catch (err) {
            console.error('Error updating Discord username:', err);
            toast.error('Failed to update Discord username: ' + err.message);
        }
    };

    const handleDisconnectGoogle = async () => {
        try {
            await unlinkSocialProvider('google');
            setCurrentUser(prev => ({ ...prev, googleUserID: null }));
            toast.success('Google account disconnected');
        } catch (err) {
            toast.error('Failed to disconnect: ' + err.message);
        }
    };

    const handleConnectGoogle = async () => {
        try {
            const url = await getGoogleSignInUrl('link');
            window.location.href = url;
        } catch (err) {
            toast.error('Failed to start Google linking');
        }
    };

    const handleConnectApple = async () => {
        try {
            const result = await linkAppleAccount();
            if (result.user) setCurrentUser(result.user);
            toast.success('Apple account linked successfully');
        } catch (err) {
            if (err.message !== 'Sign-in cancelled') toast.error('Failed to link Apple: ' + err.message);
        }
    };

    const handleDisconnectApple = async () => {
        try {
            await unlinkSocialProvider('apple');
            setCurrentUser(prev => ({ ...prev, appleUserID: null }));
            toast.success('Apple account disconnected');
        } catch (err) {
            toast.error('Failed to disconnect: ' + err.message);
        }
    };

    return (
        <div className="container">
            <h1 className="header">Account</h1>
            <p className="paragraph">
                <Link to="/wallet" className="button">My Wallet</Link>
                {' '}
                <Link to="/binders" className="button">My Binders</Link>
                {' '}
                <Link to="/games/user" className="button">My Games</Link>
                {' '}
                <Link to="/customizations" className="button">My Customizations</Link>
                {' '}
                <Link to={`/u/${currentUser?.username}`} className="button">View Profile</Link>
                {' '}
                <button
                    className="button"
                    onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}/u/${currentUser?.username}`);
                        toast.success('Profile link copied!');
                    }}
                >
                    Share Profile
                </button>
            </p>
            <p className="paragraph">
                <strong>Current User Details:</strong>
                <br />
                <span>
                    Experience Points: {levelInfo?.currentXP ?? 0} / {levelInfo?.nextLevelXP ?? 0}
                </span>
                <br />
                <span>Level: {levelInfo?.level ?? 1}</span>
            </p>
            <div className="paragraph">
                <strong>Items:</strong>
                <div className="account-items">
                    <span className="account-item-row tooltip-container">
                        <span className="account-item-icon energy-drink-icon">⚡</span>
                        {userItems.energy_drink || 0}{itemCaps.energy_drink ? `/${itemCaps.energy_drink}` : ''}
                        <span className="tooltip">Energy Drink — fully restores one player's stamina</span>
                    </span>
                </div>
            </div>
            <div className="paragraph">
                <strong>Trophy Case:</strong>
                <TrophyCase trophies={trophies} />
            </div>
            <p className="paragraph">
                <strong>Settings:</strong>
                <br />
                Game Animation Speed:
                <select value={gameSpeed} onChange={handleGameSpeedChange}>
                    <option value={GLOBALS.GAME_SPEED.NORMAL.name}>Normal</option>
                    <option value={GLOBALS.GAME_SPEED.FAST.name}>Fast</option>
                    <option value={GLOBALS.GAME_SPEED.FASTER.name}>Fastest</option>
                    <option value={GLOBALS.GAME_SPEED.INSTANT.name}>Instant</option>
                </select>
            </p>
            <form onSubmit={handleDiscordUsernameChange} className="paragraph">
                <label>
                    <strong>Discord Username:</strong>
                    <br />
                    <input
                        type="text"
                        value={discordUsername}
                        onChange={e => setDiscordUsername(e.target.value)}
                        placeholder="Enter your Discord username"
                    />
                </label>
                <button type="submit">Update</button>
            </form>
            <div className="connected-accounts paragraph">
                <strong>Connected Accounts:</strong>
                <div className="provider-row">
                    <img src="/google-logo.svg" alt="Google" className="provider-icon" />
                    {currentUser?.googleUserID ? (
                        <>
                            <span>Google — Connected</span>
                            {currentUser?.authOrigin !== 'google' && (
                                <button className="boring_button" onClick={handleDisconnectGoogle}>
                                    Disconnect
                                </button>
                            )}
                        </>
                    ) : (
                        <button className="less-action" onClick={handleConnectGoogle}>
                            Connect Google Account
                        </button>
                    )}
                </div>
                <div className="provider-row">
                    <span className="apple-provider-icon"></span>
                    {currentUser?.appleUserID ? (
                        <>
                            <span>Apple — Connected</span>
                            {currentUser?.authOrigin !== 'apple' && (
                                <button className="boring_button" onClick={handleDisconnectApple}>
                                    Disconnect
                                </button>
                            )}
                        </>
                    ) : (
                        <button className="less-action" onClick={handleConnectApple}>
                            Connect Apple Account
                        </button>
                    )}
                </div>
            </div>
            {(badges.earned.length > 0 || (badges.locked.bronze + badges.locked.silver + badges.locked.gold + badges.locked.diamond) > 0) && (
                <div className="paragraph">
                    <strong>Badges:</strong>
                    <BadgeCatalog badges={badges} showLocked />
                </div>
            )}
        </div>
    );
};

export default AccountPage;
